<template>
  <div class="scrollable-container" ref="container">
    <div class="scrollable-content" ref="content">
      <slot></slot>
    </div>
    <div class="custom-scroller" ref="scroller">
      <div class="scroller-thumb" ref="thumb">
        <div>
          <div></div>
          <div></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  onMounted,
  onBeforeUnmount,
  ref,
  nextTick,
  defineExpose,
  defineProps,
} from "vue";

const props = defineProps({
  minThumbHeight: {
    type: Number,
    default: 30,
  },
  externalScrollerClasses: {
    type: String,
    default: null,
  },
});

const container = ref(null);
const content = ref(null);
const thumb = ref(null);
const scroller = ref(null);

const externalScroller = ref(null);
let isDragging = false;
let startY, startTop;

const updateThumbPosition = () => {
  let containerHeight = content.value.clientHeight;
  const contentHeight = content.value.scrollHeight;
  const scrollTop = content.value.scrollTop;

  // if (cta) {
  const paddingBottom = parseFloat(
    window.getComputedStyle(content.value).paddingBottom
  );
  containerHeight = content.value.clientHeight - paddingBottom;

  if (externalScroller.value) {
    externalScroller.value.style.height = `${containerHeight}px`;
  } else if (scroller.value) {
    scroller.value.style.height = `${containerHeight}px`;
  } else {
    scroller.value.style.height = `${containerHeight}px`;
  }
  // }

  const scrollableHeight = contentHeight - content.value.clientHeight;

  const thumbHeight = Math.max(
    (containerHeight / contentHeight) * containerHeight,
    props.minThumbHeight
  );

  const thumbTop =
    scrollableHeight > 0
      ? (scrollTop / scrollableHeight) * (containerHeight - thumbHeight)
      : 0;

  thumb.value.style.height = `${thumbHeight}px`;
  thumb.value.style.top = `${thumbTop}px`;
};

const updateScrollerVisibility = () => {
  if (content.value && scroller.value) {
    scroller.value.style.display =
      content.value.scrollHeight > content.value.clientHeight
        ? "block"
        : "none";

    if (content.value.scrollHeight > content.value.clientHeight) {
      content.value.style.removeProperty("padding-right");
    } else {
      content.value.style.paddingRight = "0px";
    }
  }
};

const handleMouseDown = (e) => {
  isDragging = true;
  startY = e.clientY;
  startTop = parseInt(window.getComputedStyle(thumb.value).top, 10);
  document.body.style.userSelect = "none";
};

const handleMouseMove = (e) => {
  if (!isDragging) return;

  const deltaY = e.clientY - startY;
  const newTop = Math.max(
    0,
    Math.min(
      scroller.value.offsetHeight - thumb.value.offsetHeight,
      startTop + deltaY
    )
  );

  const scrollRatio = content.value.scrollHeight / content.value.clientHeight;
  content.value.scrollTop = newTop * scrollRatio;
  thumb.value.style.top = `${newTop}px`;
};

const handleMouseUp = () => {
  isDragging = false;
  document.body.style.userSelect = "";
};

// Expose scrolling methods
const scrollToBottom = () => {
  if (content.value) {
    content.value.scrollTo({
      top: content.value.scrollHeight,
      behavior: "smooth",
    });
  }
};

onMounted(() => {
  nextTick(() => {
    externalScroller.value = document.querySelector(
      props.externalScrollerClasses
    );
    updateScrollerVisibility();
    updateThumbPosition();

    window.addEventListener("resize", updateScrollerVisibility);
    content.value.addEventListener("scroll", () => {
      updateThumbPosition();
    });

    thumb.value.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScrollerVisibility);
  content.value.removeEventListener("scroll", updateThumbPosition);
  thumb.value.removeEventListener("mousedown", handleMouseDown);
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
});

defineExpose({
  updateScrollerVisibility,
  updateThumbPosition,
  scrollToBottom,
});
</script>
